'use client'

import useSWR from 'swr'

import { useDebounce } from './use-debounce'

export interface PlacePrediction {
    placeId: string
    description: string
    mainText: string
    secondaryText: string
}

interface PlacesResponse {
    predictions: PlacePrediction[]
}

async function fetchPredictions(url: string): Promise<PlacesResponse> {
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error('Failed to fetch place predictions')
    }
    return response.json() as Promise<PlacesResponse>
}

/**
 * `useDebounce` seeds its state with the value it is first handed, so a hook mounted
 * with text ≥ 2 chars requests predictions **immediately, undebounced** — an intentional
 * prefetch, not an oversight. `LocationCombobox`'s wide-viewport dropdown opens on focus
 * only when predictions already exist, so the prefetch is what lets a visitor focus a
 * pre-filled origin/destination and see suggestions at once. Do not gate the request on
 * focus or first edit without also opening the dropdown when predictions arrive, or a
 * pre-filled field goes quiet until the visitor types.
 */
export function usePlacesAutocomplete(input: string | undefined, locale = 'en-us') {
    const debouncedInput = useDebounce(input ?? '', 300)

    const { data, error, isLoading } = useSWR<PlacesResponse, Error>(
        debouncedInput.length >= 2
            ? `/api/nexus/places/autocomplete?input=${encodeURIComponent(debouncedInput)}&locale=${encodeURIComponent(locale)}`
            : null,
        fetchPredictions,
        { revalidateOnFocus: false, keepPreviousData: true },
    )

    return {
        predictions: data?.predictions ?? [],
        error,
        isLoading,
    }
}
